import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiPlus, FiFile, FiDownload, FiUpload, FiAlertCircle, FiEdit2, FiTrash2, FiX, FiAlertTriangle, FiInfo, FiMapPin, FiServer, FiCamera, FiCalendar, FiFileText } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
    fetchCompanyId,
    fetchCCTVHardware,
    createCCTVHardware,
    updateCCTVHardware,
    deleteCCTVHardware,
    fetchBranches,
    fetchDeployments,
    type CCTVHardware,
    type CCTVHardwareFormData
} from '../service/cctvHardwareService';
import Table, { Column } from '../../../../components/Table';

export default function ManageCCTVHardware() {
    const [hardware, setHardware] = useState<CCTVHardware[]>([]);
    const [filteredHardware, setFilteredHardware] = useState<CCTVHardware[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [regionFilter, setRegionFilter] = useState('All');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedHardware, setSelectedHardware] = useState<CCTVHardware | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedHardware, setEditedHardware] = useState<Partial<CCTVHardware> | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hardwareToDelete, setHardwareToDelete] = useState<CCTVHardware | null>(null);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [branches, setBranches] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vendorMap, setVendorMap] = useState<Map<string, string>>(new Map());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Excel upload states
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadResults, setUploadResults] = useState<any[]>([]);
    const [undoStack, setUndoStack] = useState<any[]>([]); // Store previous hardware data for undo

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const pso = useAppSelector((s) => s.user.pso);
    const designation = (pso?.designation || '').replace(/_/g, ' ').toLowerCase();
    const isCctvIncharge = designation.includes('cctv');
    const isMIS = designation.includes('mis');
    // Build candidate IDs that a branch.psoId might store for this PSO (memoized to prevent infinite loops)
    const psoIdCandidates: string[] = useMemo(() => [
        ...(pso?.authUid ? [pso.authUid] : []),
        ...(((pso as any)?.psoId) ? [(pso as any).psoId as string] : []),
        ...(uid ? [uid] : []),
    ], [pso?.authUid, (pso as any)?.psoId, uid]);
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const isIncharge = designation.includes('incharge');
    const isPSO = userRole === 'pso' && !isIncharge;

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid, isBankUser);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid, isBankUser]);

    // Fetch branches
    useEffect(() => {
        if (!companyId) return;

        const loadBranches = async () => {
            try {
                const branchesList = await fetchBranches(companyId);
                setBranches(branchesList);
            } catch (err) {
                console.error('Error fetching branches:', err);
            }
        };

        loadBranches();
    }, [companyId]);

    // Fetch hardware (only load data, filtering happens in separate useEffect)
    useEffect(() => {
        if (!companyId || branches.length === 0 || (!isBankUser && !isCctvIncharge && !isMIS && psoIdCandidates.length === 0)) return;

        const loadHardware = async () => {
            setLoading(true);
            try {
                const hardwareList = await fetchCCTVHardware(companyId);
                // Fetch ALL deployments with vendor names (not filtered by PSO) so we can show vendors for all branches
                // Pass isBankUser=true to fetchDeployments to get all deployments regardless of PSO
                const fetchedVendorMap = await fetchDeployments(companyId, uid || '', true);

                setVendorMap(fetchedVendorMap);
                setHardware(hardwareList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to load CCTV hardware');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadHardware();
    }, [companyId, branches.length, isBankUser, isCctvIncharge, isMIS, psoIdCandidates.length, uid]);

    // Filter and merge hardware (runs when hardware, branches, or filters change)
    useEffect(() => {
        // For bank users, CCTV incharge, and MIS, allow filtering even if vendorMap is empty
        const canProceedWithoutVendorMap = isBankUser || isCctvIncharge || isMIS;
        if (!companyId || branches.length === 0 || (!canProceedWithoutVendorMap && vendorMap.size === 0) || (!isBankUser && !isCctvIncharge && !isMIS && psoIdCandidates.length === 0)) {
            // If hardware is still loading, don't clear the list
            if (loading) return;
            // Otherwise set empty list
            setFilteredHardware([]);
            return;
        }

        // Filter branches to only show those assigned to this PSO (unless bank user, CCTV incharge, or MIS)
        // For regular PSO users, show branches where psoId matches OR where psoId is not set (unassigned)
        const assignedBranches = branches.filter((branch: any) => {
            if (isBankUser || isCctvIncharge || isMIS) return true;
            // If branch has a psoId, it must match one of the current user's PSO IDs
            if (branch.psoId) {
                return psoIdCandidates.includes(branch.psoId);
            }
            // If branch has no psoId, show it (unassigned branches)
            return true;
        });

        // Merge branches with hardware data
        const hardwareMap = new Map();
        hardware.forEach(hw => {
            if (hw.branchId && !hardwareMap.has(hw.branchId)) {
                hardwareMap.set(hw.branchId, hw);
            }
        });

        let list = assignedBranches.map(branch => {
            const existingHardware = hardwareMap.get(branch.id);
            // Get vendor name from deployment vendors (vendorMap contains all deployments)
            const vendorNameFromDeployment = (vendorMap && vendorMap.get) ? vendorMap.get(branch.id) || '' : '';
            const regionName = branch.locationName || '';

            if (existingHardware) {
                // Prefer vendor from deployment vendors, fallback to hardware vendor field
                const finalVendorName = vendorNameFromDeployment || (existingHardware as any).vendor || (existingHardware as any).vendorName || '';
                return {
                    ...existingHardware,
                    vendorName: finalVendorName,
                    regionName: regionName || (existingHardware as any).regionName || '',
                };
            }
            return {
                id: `new-${branch.id}`,
                branchId: branch.id,
                branchCode: branch.branchCode || '',
                branchName: branch.address || '',
                cluster: '',
                region: '',
                vendor: '',
                vendorName: vendorNameFromDeployment,
                regionName: regionName,
                dvrNvr: '',
                noOfDvrNvr: 0,
                noOfCamera: 0,
                hdAnalog: '',
                noOf5MpCamera: 0,
                noOfHdd: 0,
                installationStatus: 'pending' as const,
                installationDate: '',
                location: '',
                remarks: '',
                companyId: companyId,
            } as CCTVHardware & { vendorName?: string; regionName?: string };
        });

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(item => {
                const branchCode = (item.branchCode || '').toString().toLowerCase();
                const branchName = (item.branchName || '').toString().toLowerCase();
                const vendorName = ((item as any).vendorName || '').toString().toLowerCase();
                const regionName = ((item as any).regionName || '').toString().toLowerCase();
                const cluster = (item.cluster || '').toString().toLowerCase();
                const region = (item.region || '').toString().toLowerCase();
                const vendor = (item.vendor || '').toString().toLowerCase();
                const location = (item.location || '').toString().toLowerCase();
                const remarks = (item.remarks || '').toString().toLowerCase();
                const installationStatus = (item.installationStatus || '').toString().toLowerCase();
                return branchCode.includes(term) ||
                    branchName.includes(term) ||
                    vendorName.includes(term) ||
                    regionName.includes(term) ||
                    cluster.includes(term) ||
                    region.includes(term) ||
                    vendor.includes(term) ||
                    location.includes(term) ||
                    remarks.includes(term) ||
                    installationStatus.includes(term);
            });
        }

        // Apply region filter
        if (regionFilter !== 'All') {
            list = list.filter(item => {
                const itemRegion = ((item as any).regionName || item.region || '').toString().toLowerCase();
                return itemRegion === regionFilter.toLowerCase();
            });
        }

        setFilteredHardware(list);
        setCurrentPage(1);
    }, [hardware, branches, searchTerm, regionFilter, companyId, isBankUser, isCctvIncharge, isMIS, psoIdCandidates, vendorMap, loading]);

    const totalPages = Math.ceil(filteredHardware.length / perPage);
    const paginatedData = filteredHardware.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        if (typeof date === 'string') {
            return new Date(date).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'installed') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'sent') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status}
            </span>
        );
    };

    // Export to Excel
    const exportToExcel = () => {
        if (filteredHardware.length === 0) {
            setToastMessage('No hardware to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const worksheetData = filteredHardware.map(item => ({
            'Branch Code': item.branchCode || 'N/A',
            'Branch Name': item.branchName || 'N/A',
            'Cluster': item.cluster || 'N/A',
            'Region': (item as any).regionName || item.region || 'N/A',
            'Vendor Name': (item as any).vendorName || item.vendor || 'N/A',
            'DVR/NVR': item.dvrNvr || 'N/A',
            'NO. OF DVR/NVR': item.noOfDvrNvr || 0,
            'NO. OF CAMERA': item.noOfCamera || 0,
            'HD/ANALOG': item.hdAnalog || 'N/A',
            'NO. OF 2 MP CAMERA': item.noOf2MpCamera || 0,
            'NO. OF 5 MP CAMERA': item.noOf5MpCamera || 0,
            'NO. OF 6 MP CAMERA': item.noOf6MpCamera || 0,
            'NO. OF HDD': item.noOfHdd || 0,
            'INSTALLATION DATE': item.installationDate || 'N/A',
            'VISIT DATE': item.visitDate || 'N/A',
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'CCTV Hardware');

        const fileName = `CCTV_Hardware_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const exportToPDF = () => {
        if (filteredHardware.length === 0) {
            setToastMessage('No hardware to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for more columns
        doc.setFontSize(20);
        doc.text('CCTV Hardware Inventory', 14, 15);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

        const tableData = filteredHardware.map(item => [
            item.branchCode || 'N/A',
            item.branchName || 'N/A',
            (item as any).regionName || item.region || 'N/A',
            (item as any).vendorName || item.vendor || 'N/A',
            item.dvrNvr || 'N/A',
            item.noOfDvrNvr || 0,
            item.noOfCamera || 0,
            item.hdAnalog || 'N/A',
            item.noOfHdd || 0,
            item.installationStatus || 'N/A',
            item.installationDate || 'N/A'
        ]);

        autoTable(doc, {
            head: [['Branch Code', 'Branch Name', 'Region', 'Vendor', 'DVR/NVR', 'Qty', 'Cameras', 'Type', 'HDD', 'Status', 'Date']],
            body: tableData,
            startY: 30,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [52, 152, 219] },
            alternateRowStyles: { fillColor: [245, 245, 245] }
        });

        const fileName = `CCTV_Hardware_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF report generated successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Download sample Excel format
    const downloadSampleFormat = () => {
        const sampleData = [
            {
                'Branch Code': '1895',
                'Cluster': 'South',
                'DVR/NVR': 'DVR',
                'NO. OF DVR/NVR': 2,
                'HD/ANALOG': 'HD',
                'NO. OF 2 MP CAMERA': 4,
                'NO. OF 5 MP CAMERA': 8,
                'NO. OF 6 MP CAMERA': 4,
                'NO. OF HDD': 4,
                'INSTALLATION DATE': '2024-01-01',
                'VISIT DATE': '2024-02-01',
                'REMARKS': 'Initial setup completed'
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'CCTV Hardware Sample');

        const fileName = `CCTV_Hardware_Sample_Format.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Sample Excel format downloaded successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Import from Excel
    const handleImportExcel = () => {
        fileInputRef.current?.click();
    };

    const processExcelFile = async (file: File) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadResults([]);
        setUndoStack([]);

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

            const results: any[] = [];
            const undoData: any[] = [];

            // Fetch existing hardware to check for updates
            const existingHardware = await fetchCCTVHardware(companyId);
            const hardwareByBranchId = new Map<string, CCTVHardware>();
            existingHardware.forEach(hw => {
                if (hw.branchId && !hardwareByBranchId.has(hw.branchId)) {
                    hardwareByBranchId.set(hw.branchId, hw);
                }
            });

            const totalRows = jsonData.length;

            for (let index = 0; index < jsonData.length; index++) {
                const row = jsonData[index];
                const result: any = {
                    rowNumber: index + 2, // +2 because Excel rows start at 1 and we skip header
                    branchCode: row['Branch Code'] || '',
                    status: 'pending',
                    message: ''
                };

                try {
                    // Update progress
                    setUploadProgress(Math.round(((index + 1) / totalRows) * 100));

                    // Find branch by code
                    const branch = branches.find(b =>
                        String(b.branchCode || '').toLowerCase() === String(row['Branch Code'] || '').toLowerCase()
                    );

                    if (!branch) {
                        result.status = 'error';
                        result.message = `Branch with code "${row['Branch Code']}" not found`;
                        results.push(result);
                        continue;
                    }

                    // Get vendor name from deployment vendors (vendorMap contains all deployments)
                    const vendorName = (vendorMap && vendorMap.get) ? vendorMap.get(branch.id) || '' : '';

                    // Auto-populate branch details from branch data
                    const noOf2Mp = parseInt(row['NO. OF 2 MP CAMERA'] || row['NO OF 2 MP CAMERA'] || '0') || 0;
                    const noOf5Mp = parseInt(row['NO. OF 5 MP CAMERA'] || row['NO OF 5 MP CAMERA'] || '0') || 0;
                    const noOf6Mp = parseInt(row['NO. OF 6 MP CAMERA'] || row['NO OF 6 MP CAMERA'] || '0') || 0;

                    const hardwareData: Partial<CCTVHardware> = {
                        branchId: branch.id,
                        branchCode: branch.branchCode,
                        branchName: branch.name || branch.address,
                        cluster: row['CLUSTER'] || row['Cluster'] || branch.cluster || '',
                        region: (branch as any).locationName || branch.region || '',
                        vendor: vendorName,
                        dvrNvr: row['DVR/NVR'] || '',
                        noOfDvrNvr: parseInt(row['NO. OF DVR/NVR'] || row['NO OF DVR/NVR'] || '0') || 0,
                        noOfCamera: noOf2Mp + noOf5Mp + noOf6Mp,
                        hdAnalog: row['HD/ANALOG'] || '',
                        noOf2MpCamera: noOf2Mp,
                        noOf5MpCamera: noOf5Mp,
                        noOf6MpCamera: noOf6Mp,
                        noOfHdd: parseInt(row['NO. OF HDD'] || row['NO OF HDD'] || '0') || 0,
                        installationDate: row['INSTALLATION DATE'] || row['Installation Date'] || '',
                        visitDate: row['VISIT DATE'] || row['Visit Date'] || '',
                        remarks: row['REMARKS'] || row['Remarks'] || '',
                        installationStatus: 'pending',
                    };

                    // Check if hardware already exists for this branch
                    const existingHw = hardwareByBranchId.get(branch.id);
                    if (existingHw && !existingHw.id.startsWith('new-')) {
                        // Store undo data before updating
                        undoData.push({
                            hardwareId: existingHw.id,
                            previousData: {
                                dvrNvr: existingHw.dvrNvr,
                                noOfDvrNvr: existingHw.noOfDvrNvr,
                                noOfCamera: existingHw.noOfCamera,
                                hdAnalog: existingHw.hdAnalog,
                                noOf2MpCamera: existingHw.noOf2MpCamera,
                                noOf5MpCamera: existingHw.noOf5MpCamera,
                                noOf6MpCamera: existingHw.noOf6MpCamera,
                                noOfHdd: existingHw.noOfHdd,
                                installationDate: existingHw.installationDate,
                                visitDate: existingHw.visitDate,
                            }
                        });

                        // Update existing hardware
                        await updateCCTVHardware(companyId, existingHw.id, hardwareData);
                        result.status = 'updated';
                        result.message = `Hardware updated for branch ${branch.branchCode}`;
                        result.hardwareId = existingHw.id;
                    } else {
                        // Create new hardware
                        const hardwareId = await createCCTVHardware(companyId, hardwareData);
                        result.status = 'success';
                        result.message = `Hardware created for branch ${branch.branchCode}`;
                        result.hardwareId = hardwareId;
                    }

                    results.push(result);
                } catch (err: any) {
                    console.error('Error importing row:', err);
                    result.status = 'error';
                    result.message = err.message || 'Failed to process row';
                    results.push(result);
                }
            }

            setUploadResults(results);
            setUndoStack(undoData);
            setShowUploadModal(true);

            const successCount = results.filter(r => r.status === 'success').length;
            const updatedCount = results.filter(r => r.status === 'updated').length;
            const errorCount = results.filter(r => r.status === 'error').length;

            if (successCount > 0 || updatedCount > 0) {
                setToastMessage(`✅ Import completed: ${successCount} new, ${updatedCount} updated, ${errorCount} failed`);
                setToastType('success');
            } else {
                setToastMessage(`❌ Import failed: ${errorCount} errors`);
                setToastType('error');
            }
            setShowToast(true);
        } catch (err: any) {
            console.error('Error processing Excel file:', err);
            setToastMessage('Failed to import Excel file');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processExcelFile(file);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleModalClose = () => {
        // Auto-keep if there are updates and modal is closed
        if (undoStack.length > 0 && uploadResults.some(r => r.status === 'updated')) {
            // Already kept (no undo action taken)
            setUndoStack([]);
        }
        setShowUploadModal(false);
        // Refresh hardware list
        if (companyId) {
            fetchCCTVHardware(companyId).then(hardwareList => {
                setHardware(hardwareList);
                setFilteredHardware(hardwareList);
            });
        }
    };

    const handleUndo = async () => {
        if (!companyId || undoStack.length === 0) return;

        setUploading(true);
        try {
            // Undo all updates
            for (const undoItem of undoStack) {
                if (undoItem.hardwareId && undoItem.previousData) {
                    await updateCCTVHardware(companyId, undoItem.hardwareId, undoItem.previousData);
                }
            }

            // Update results to show undone
            setUploadResults(prev => prev.map(r => {
                if (r.status === 'updated') {
                    return { ...r, status: 'undone', message: `Undone: ${r.message}` };
                }
                return r;
            }));

            setUndoStack([]);
            setToastMessage('✅ Changes undone successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (error: any) {
            console.error('Undo error:', error);
            setToastMessage('❌ Failed to undo changes');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUploading(false);
        }
    };

    const handleKeep = () => {
        setUndoStack([]);
        setShowUploadModal(false);
        setToastMessage('✅ Changes kept');
        setToastType('success');
        setShowToast(true);

        // Refresh hardware list
        if (companyId) {
            fetchCCTVHardware(companyId).then(hardwareList => {
                setHardware(hardwareList);
                setFilteredHardware(hardwareList);
            });
        }
    };

    const downloadErrorReport = () => {
        const errorRows = uploadResults.filter(r => r.status === 'error');
        if (errorRows.length === 0) return;

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(errorRows.map(row => ({
            'Row Number': row.rowNumber,
            'Branch Code': row.branchCode,
            'Error Message': row.message
        })));
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');
        XLSX.writeFile(workbook, 'cctv_hardware_errors.xlsx');
    };

    const downloadFinalReport = () => {
        const workbook = XLSX.utils.book_new();
        const resultsData = uploadResults.map(row => ({
            'Row Number': row.rowNumber,
            'Branch Code': row.branchCode,
            'Status': row.status,
            'Message': row.message
        }));
        const resultsWorksheet = XLSX.utils.json_to_sheet(resultsData);
        XLSX.utils.book_append_sheet(workbook, resultsWorksheet, 'Results');

        const summaryData = [
            { 'Metric': 'Total Rows', 'Count': uploadResults.length },
            { 'Metric': 'New Hardware', 'Count': uploadResults.filter(r => r.status === 'success').length },
            { 'Metric': 'Updated', 'Count': uploadResults.filter(r => r.status === 'updated').length },
            { 'Metric': 'Failed', 'Count': uploadResults.filter(r => r.status === 'error').length },
        ];
        const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

        XLSX.writeFile(workbook, 'cctv_hardware_final_report.xlsx');
    };

    // Form data
    const [formData, setFormData] = useState<Partial<CCTVHardwareFormData>>({
        branchId: '',
        cluster: '',
        region: '',
        vendor: '',
        dvrNvr: '',
        noOfDvrNvr: 0,
        noOfCamera: 0,
        hdAnalog: '',
        noOf2MpCamera: 0,
        noOf6MpCamera: 0,
        noOf5MpCamera: 0,
        noOfHdd: 0,
        installationDate: new Date().toISOString().split('T')[0],
        visitDate: '',
        installationStatus: 'pending',
        location: '',
        remarks: '',
    });

    // Auto-calculate Total Camera Count for Add Form
    useEffect(() => {
        const total = (formData.noOf2MpCamera || 0) + (formData.noOf5MpCamera || 0) + (formData.noOf6MpCamera || 0);
        if (formData.noOfCamera !== total) {
            setFormData(prev => ({ ...prev, noOfCamera: total }));
        }
    }, [formData.noOf2MpCamera, formData.noOf5MpCamera, formData.noOf6MpCamera, formData.noOfCamera]);

    // Auto-calculate Total Camera Count for Edit Form
    useEffect(() => {
        if (!editedHardware) return;
        const total = (editedHardware.noOf2MpCamera || 0) + (editedHardware.noOf5MpCamera || 0) + (editedHardware.noOf6MpCamera || 0);
        if (editedHardware.noOfCamera !== total) {
            setEditedHardware(prev => prev ? { ...prev, noOfCamera: total } : null);
        }
    }, [editedHardware?.noOf2MpCamera, editedHardware?.noOf5MpCamera, editedHardware?.noOf6MpCamera, editedHardware?.noOfCamera]);

    // Reset form
    const resetForm = () => {
        setFormData({
            branchId: '',
            cluster: '',
            region: '',
            vendor: '',
            dvrNvr: '',
            noOfDvrNvr: 0,
            noOfCamera: 0,
            hdAnalog: '',
            noOf2MpCamera: 0,
            noOf6MpCamera: 0,
            noOf5MpCamera: 0,
            noOfHdd: 0,
            installationDate: new Date().toISOString().split('T')[0],
            visitDate: '',
            installationStatus: 'pending',
            location: '',
            remarks: '',
        });
    };

    // Handle add hardware
    const handleAddHardware = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.branchId) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        try {
            await createCCTVHardware(companyId, formData as Partial<CCTVHardware>);

            setToastMessage('CCTV hardware added successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            resetForm();

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to add CCTV hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle view details
    const handleViewDetails = (item: CCTVHardware) => {
        setSelectedHardware(item);
        setEditedHardware({ ...item });
        setIsEditing(false); // Ensure it opens in View mode
        setShowDetailsModal(true);
    };

    // Handle edit
    const handleEdit = () => {
        if (selectedHardware) {
            setIsEditing(true);
            setEditedHardware({ ...selectedHardware });
        }
    };

    // Handle delete
    const handleDeleteClick = (item: CCTVHardware) => {
        setHardwareToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!companyId || !hardwareToDelete) return;

        setDeleting(true);
        try {
            await deleteCCTVHardware(companyId, hardwareToDelete.id);

            setToastMessage('Hardware deleted successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);

            if (selectedHardware?.id === hardwareToDelete.id) {
                setShowDetailsModal(false);
                setSelectedHardware(null);
            }

            setShowDeleteModal(false);
            setHardwareToDelete(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to delete hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setDeleting(false);
        }
    };

    // Calculate summary statistics

    const columns: Column<CCTVHardware>[] = [

        {
            header: 'Branch Code',
            accessor: 'branchCode',
            align: 'center',
            className: 'sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 !px-4',
            headerClassName: 'sticky left-0 z-20 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 font-bold !px-4',
            render: (item) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {item.branchCode || 'N/A'}
                </span>
            )
        },


        {
            header: 'Branch Name',
            accessor: 'branchName',
            className: 'whitespace-normal break-words max-w-[150px]',
            render: (item) => item.branchName || 'N/A'
        },
        { header: 'Cluster', accessor: 'cluster', render: (item) => item.cluster || 'N/A' },
        {
            header: 'Region',
            accessor: 'region',
            render: (item) => (item as any).regionName || item.region || 'N/A'
        },
        {
            header: 'Vendor',
            accessor: 'vendor',
            className: 'whitespace-normal break-words max-w-[120px]',
            render: (item) => (item as any).vendorName || 'N/A'
        },
        { header: 'DVR/NVR', accessor: 'dvrNvr', align: 'center', render: (item) => item.dvrNvr || 'N/A' },
        { header: '# DVR/NVR', accessor: 'noOfDvrNvr', align: 'center', render: (item) => item.noOfDvrNvr || 0 },
        { header: '# Camera', accessor: 'noOfCamera', align: 'center', render: (item) => item.noOfCamera || 0 },
        { header: 'HD/Analog', accessor: 'hdAnalog', align: 'center', render: (item) => item.hdAnalog || 'N/A' },
        {
            header: 'CAMERAS BY MP',
            accessor: 'noOf5MpCamera',
            align: 'center',
            render: (item) => (
                <div className="flex flex-col text-xs leading-tight">
                    {(item.noOf2MpCamera ?? 0) > 0 && <span className="whitespace-nowrap">2MP: {item.noOf2MpCamera}</span>}
                    {(item.noOf5MpCamera ?? 0) > 0 && <span className="whitespace-nowrap">5MP: {item.noOf5MpCamera}</span>}
                    {(item.noOf6MpCamera ?? 0) > 0 && <span className="whitespace-nowrap">6MP: {item.noOf6MpCamera}</span>}
                    {!(item.noOf2MpCamera || item.noOf5MpCamera || item.noOf6MpCamera) && <span>0</span>}
                </div>
            )
        },
        { header: '# HDD', accessor: 'noOfHdd', align: 'center', render: (item) => item.noOfHdd || 0 },
        { header: 'Install. Date', accessor: 'installationDate', render: (item) => item.installationDate ? formatDate(item.installationDate) : 'N/A' },
        { header: 'Visit Date', accessor: 'visitDate', render: (item) => item.visitDate ? formatDate(item.visitDate) : 'N/A' },
        {
            header: 'Actions',
            accessor: 'id',
            align: 'center',
            width: '80px',
            className: 'sticky right-0 z-10 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] !px-2',
            headerClassName: 'sticky right-0 z-20 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 font-bold !px-2',
            render: (item) => (
                <div className="flex gap-1.5 justify-center">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(item);
                        }}
                        className="inline-flex items-center justify-center rounded border border-gray-200 bg-white p-1.5 text-gray-600 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedHardware(item);
                            setEditedHardware({ ...item });
                            setIsEditing(true);
                            setShowDetailsModal(true);
                        }}
                        className="inline-flex items-center justify-center rounded border border-gray-200 bg-white p-1.5 text-gray-600 transition-all hover:border-green-500 hover:bg-green-50 hover:text-green-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                        title="Edit"
                    >
                        <Icon icon={FiEdit2} className="h-4 w-4" />
                    </button>
                </div>
            )
        }


    ];

    const headerActions = (
        <div className="flex gap-3">
            {isBankUser && (
                <>
                    <Button variant="secondary" onClick={handleImportExcel} className="flex items-center gap-2">
                        <Icon icon={FiUpload} className="h-4 w-4" />
                        Import Excel
                    </Button>
                    <Button variant="secondary" onClick={downloadSampleFormat} className="flex items-center gap-2">
                        <Icon icon={FiDownload} className="h-4 w-4" />
                        Download Sample
                    </Button>
                </>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by branch code, address, region, vendor name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            <div className="relative">
                <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="All">All Regions</option>
                    {Array.from(new Set(
                        branches
                            .filter((branch: any) => {
                                if (isBankUser || isCctvIncharge || isMIS) return true;
                                if (branch.psoId) {
                                    return psoIdCandidates.includes(branch.psoId);
                                }
                                return true;
                            })
                            .map((branch: any) => branch.locationName || '')
                            .filter((region: string) => region.trim() !== '')
                    ))
                        .sort((a, b) => a.localeCompare(b))
                        .map((region) => (
                            <option key={region} value={region}>{region}</option>
                        ))}
                </select>
            </div>
            {(searchTerm || regionFilter !== 'All') && (
                <Button
                    variant="secondary"
                    onClick={() => {
                        setSearchTerm('');
                        setRegionFilter('All');
                        setCurrentPage(1);
                    }}
                    className="text-sm whitespace-nowrap"
                >
                    Clear Search
                </Button>
            )}
        </div>
    );

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
            />
            <div className="space-y-6">
                {/* Toast */}
                {showToast && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toastMessage}
                                type={toastType}
                                onClose={() => setShowToast(false)}
                            />
                        </div>
                    </div>
                )}


                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No hardware found matching your search' : 'No hardware available'}
                    headerActions={headerActions}
                    filters={filters}
                    title="Manage CCTV Hardware"
                    description="Track CCTV hardware installations and shipments"
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    onRowClick={handleViewDetails}
                    draggable={true}
                />
            </div>









            {/* Add Hardware Modal */}
            {
                showAddModal && (
                    <Modal
                        isOpen={showAddModal}
                        onClose={() => {
                            setShowAddModal(false);
                            resetForm();
                        }}
                        title="Add CCTV Hardware"
                        size="lg"
                    >
                        <div className="p-6">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                handleAddHardware();
                            }}>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={formData.branchId || ''}
                                                onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                required
                                            >
                                                <option value="">Select Branch</option>
                                                {branches
                                                    .filter((branch: any) => {
                                                        if (isBankUser || isCctvIncharge || isMIS) return true;
                                                        // If branch has a psoId, it must match one of the current user's PSO IDs
                                                        if (branch.psoId) {
                                                            return psoIdCandidates.includes(branch.psoId);
                                                        }
                                                        // If branch has no psoId, show it (unassigned branches)
                                                        return true;
                                                    })
                                                    .map((branch) => (
                                                        <option key={branch.id} value={branch.id}>{branch.branchCode} - {branch.address}</option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Installation Status <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={formData.installationStatus || 'pending'}
                                                onChange={(e) => setFormData({ ...formData, installationStatus: e.target.value as any })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                required
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="sent">Sent</option>
                                                <option value="installed">Installed</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Cluster
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.cluster || ''}
                                                onChange={(e) => setFormData({ ...formData, cluster: e.target.value })}
                                                placeholder="Enter cluster"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Region
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.region || ''}
                                                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                                                placeholder="Enter region"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                DVR / NVR MODEL
                                            </label>
                                            <select
                                                value={formData.dvrNvr || ''}
                                                onChange={(e) => setFormData({ ...formData, dvrNvr: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="DVR">DVR</option>
                                                <option value="NVR">NVR</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF DVR/NVR
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfDvrNvr || 0}
                                                onChange={(e) => setFormData({ ...formData, noOfDvrNvr: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                TOTAL CAMERAS (Auto-calculated)
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfCamera || 0}
                                                disabled
                                                className="bg-gray-100 cursor-not-allowed border-gray-200"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                HD/ANALOG
                                            </label>
                                            <select
                                                value={formData.hdAnalog || ''}
                                                onChange={(e) => setFormData({ ...formData, hdAnalog: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="HD">HD</option>
                                                <option value="ANALOG">ANALOG</option>
                                                <option value="IP">IP</option>

                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF 2 MP CAMERA
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOf2MpCamera || 0}
                                                onChange={(e) => setFormData({ ...formData, noOf2MpCamera: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF 5 MP CAMERA
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOf5MpCamera || 0}
                                                onChange={(e) => setFormData({ ...formData, noOf5MpCamera: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF 6 MP CAMERA
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOf6MpCamera || 0}
                                                onChange={(e) => setFormData({ ...formData, noOf6MpCamera: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF HDD
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfHdd || 0}
                                                onChange={(e) => setFormData({ ...formData, noOfHdd: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Installation Date
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.installationDate || ''}
                                                onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Visit Date
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.visitDate || ''}
                                                onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Remarks
                                        </label>
                                        <textarea
                                            value={formData.remarks || ''}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            rows={3}
                                            placeholder="Enter any additional remarks..."
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Remarks
                                        </label>
                                        <textarea
                                            value={formData.remarks || ''}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            rows={3}
                                            placeholder="Enter remarks"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowAddModal(false);
                                            resetForm();
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add Hardware'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Modal>
                )
            }

            {/* Details Modal */}
            {
                showDetailsModal && selectedHardware && editedHardware && (
                    <Modal
                        isOpen={showDetailsModal}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedHardware(null);
                            setIsEditing(false);
                            setEditedHardware(null);
                        }}
                        title={(selectedHardware as any).vendorName || selectedHardware.vendor || 'Hardware Details'}
                        size="xl"
                        headerActions={!isEditing && (isPSO || isBankUser || isCctvIncharge || isMIS) && (
                            <button
                                onClick={handleEdit}
                                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                                title="Edit Details"
                            >
                                <Icon icon={FiEdit2} className="h-5 w-5" />
                            </button>
                        )}
                    >
                        <div className="relative rounded-b-2xl bg-gray-50 dark:bg-gray-900/50">
                            <div className="mx-8 pt-6 mb-8 space-y-6">
                                {/* Section 1: Branch Information */}
                                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                                    <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                                        <Icon icon={FiMapPin} className="h-5 w-5 text-blue-600" />
                                        Location & Assignment
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Branch Name</label>
                                            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.branchName || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Branch Code</label>
                                            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 font-mono">{selectedHardware.branchCode || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Vendor</label>
                                            <p className="text-base font-semibold text-blue-600 dark:text-blue-400">{(selectedHardware as any).vendorName || selectedHardware.vendor || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Region</label>
                                            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{(selectedHardware as any).regionName || selectedHardware.region || 'N/A'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Cluster</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="text"
                                                    value={editedHardware.cluster || ''}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, cluster: e.target.value })}
                                                    className="h-10 border-blue-500/30 focus:border-blue-500"
                                                    placeholder="Enter cluster"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.cluster || 'N/A'}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Hardware Configuration */}
                                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                                    <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                                        <Icon icon={FiServer} className="h-5 w-5 text-indigo-600" />
                                        Recording & Storage
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">DVR / NVR Type</label>
                                            {isEditing ? (
                                                <select
                                                    value={editedHardware.dvrNvr || ''}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, dvrNvr: e.target.value })}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                                >
                                                    <option value="">Type</option>
                                                    <option value="DVR">DVR</option>
                                                    <option value="NVR">NVR</option>
                                                </select>
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.dvrNvr || 'N/A'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">No. of Devices</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    value={editedHardware.noOfDvrNvr || 0}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, noOfDvrNvr: parseInt(e.target.value) || 0 })}
                                                    className="h-10"
                                                    min="0"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.noOfDvrNvr || 0}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Storage (HDD)</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    value={editedHardware.noOfHdd || 0}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, noOfHdd: parseInt(e.target.value) || 0 })}
                                                    className="h-10"
                                                    min="0"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.noOfHdd || 0}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Technology</label>
                                            {isEditing ? (
                                                <select
                                                    value={editedHardware.hdAnalog || ''}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, hdAnalog: e.target.value })}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                                >
                                                    <option value="">Type</option>
                                                    <option value="HD">HD</option>
                                                    <option value="ANALOG">ANALOG</option>
                                                    <option value="IP">IP</option>
                                                </select>
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.hdAnalog || 'N/A'}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 3: Camera Details */}
                                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                                    <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                                        <Icon icon={FiCamera} className="h-5 w-5 text-emerald-600" />
                                        Camera Inventory
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Cameras</label>
                                            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900/40 p-2 rounded-lg border border-gray-100 dark:border-gray-700/50">{isEditing ? editedHardware.noOfCamera : selectedHardware.noOfCamera || 0}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">2MP Count</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    value={editedHardware.noOf2MpCamera || 0}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, noOf2MpCamera: parseInt(e.target.value) || 0 })}
                                                    className="h-10"
                                                    min="0"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.noOf2MpCamera || 0}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">5MP Count</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    value={editedHardware.noOf5MpCamera || 0}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, noOf5MpCamera: parseInt(e.target.value) || 0 })}
                                                    className="h-10"
                                                    min="0"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.noOf5MpCamera || 0}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">6MP Count</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="number"
                                                    value={editedHardware.noOf6MpCamera || 0}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, noOf6MpCamera: parseInt(e.target.value) || 0 })}
                                                    className="h-10"
                                                    min="0"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.noOf6MpCamera || 0}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 4: Timeline & Dates */}
                                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                                    <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                                        <Icon icon={FiCalendar} className="h-5 w-5 text-purple-600" />
                                        Important Dates
                                    </h3>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Installation Date</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="date"
                                                    value={editedHardware.installationDate || ''}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, installationDate: e.target.value })}
                                                    className="h-10"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.installationDate ? formatDate(selectedHardware.installationDate) : 'N/A'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Last Visit Date</label>
                                            {isEditing ? (
                                                <TextField
                                                    type="date"
                                                    value={editedHardware.visitDate || ''}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, visitDate: e.target.value })}
                                                    className="h-10"
                                                />
                                            ) : (
                                                <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedHardware.visitDate ? formatDate(selectedHardware.visitDate) : 'N/A'}</p>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Installation Status</label>
                                            {isEditing ? (
                                                <select
                                                    value={editedHardware.installationStatus || 'pending'}
                                                    onChange={(e) => setEditedHardware({ ...editedHardware, installationStatus: e.target.value as any })}
                                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-semibold text-gray-900 outline-none transition dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="sent">Sent</option>
                                                    <option value="installed">Installed</option>
                                                </select>
                                            ) : (
                                                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${selectedHardware.installationStatus === 'installed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                                    selectedHardware.installationStatus === 'sent' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                                        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                    }`}>
                                                    {selectedHardware.installationStatus || 'Pending'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Remarks Section */}
                                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                                    <h3 className="mb-5 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                                        <Icon icon={FiFileText} className="h-5 w-5 text-gray-600" />
                                        Remarks
                                    </h3>
                                    <div className="space-y-1">
                                        {isEditing ? (
                                            <textarea
                                                value={editedHardware.remarks || ''}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, remarks: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                                rows={3}
                                                placeholder="Enter remarks..."
                                            />
                                        ) : (
                                            <p className="text-base font-medium text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-900/30 p-3 rounded-lg border border-gray-100 dark:border-gray-700">{selectedHardware.remarks || 'No remarks provided.'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 bg-white px-8 py-5 border-t border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                                {isEditing ? (
                                    <>
                                        <Button
                                            variant="secondary"
                                            onClick={() => setIsEditing(false)}
                                            className="px-6 font-bold"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            className="px-8 font-bold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30"
                                            onClick={async () => {
                                                if (!companyId || !editedHardware) return;
                                                setIsSubmitting(true);
                                                try {
                                                    const updateData: Partial<CCTVHardware> = { ...editedHardware };
                                                    delete (updateData as any).id;
                                                    delete (updateData as any).regionName;
                                                    delete (updateData as any).vendorName;
                                                    delete (updateData as any).region;
                                                    delete (updateData as any).vendor;

                                                    // If ID starts with 'new-', it's a placeholder, so create it instead of updating
                                                    if (selectedHardware.id.startsWith('new-')) {
                                                        const newId = await createCCTVHardware(companyId, updateData);
                                                        setToastMessage('Hardware created successfully');
                                                    } else {
                                                        await updateCCTVHardware(companyId, selectedHardware.id, updateData);
                                                        setToastMessage('Hardware updated successfully');
                                                    }

                                                    setToastType('success');
                                                    setShowToast(true);
                                                    setIsEditing(false);

                                                    const hardwareList = await fetchCCTVHardware(companyId);
                                                    setHardware(hardwareList);
                                                    setSelectedHardware({ ...selectedHardware, ...editedHardware });
                                                    setEditedHardware(null);
                                                    setShowDetailsModal(false);
                                                } catch (err) {
                                                    console.error(err);
                                                    setToastMessage('Failed to save hardware');
                                                    setToastType('error');
                                                    setShowToast(true);
                                                } finally {
                                                    setIsSubmitting(false);
                                                }
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                                                    <span>Saving...</span>
                                                </div>
                                            ) : 'Save Changes'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowDetailsModal(false)}
                                        className="px-8 font-bold"
                                    >
                                        Close
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && hardwareToDelete && (
                    <Modal
                        isOpen={showDeleteModal}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setHardwareToDelete(null);
                        }}
                        title="Confirm Deletion"
                        size="md"
                    >
                        <div className="p-6">
                            <div className="mb-4 text-center">
                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                                    <Icon icon={FiAlertCircle} className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Are you sure?</h3>
                                <p className="mb-1 text-sm text-gray-600 dark:text-gray-400">Do you really want to delete this hardware record?</p>
                                <p className="mb-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    <span className="font-semibold">Branch:</span> {hardwareToDelete.branchCode || 'N/A'}
                                    <br />
                                    <span className="font-semibold">Item:</span> {hardwareToDelete.itemName || 'N/A'}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400">This action cannot be undone.</p>
                            </div>
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setHardwareToDelete(null);
                                    }}
                                    disabled={deleting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                                >
                                    {deleting ? 'Deleting...' : (
                                        <>
                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                            Delete
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Upload Progress Modal */}
            {
                uploading && (
                    <Modal
                        isOpen={uploading}
                        onClose={() => { }} // Prevent closing during upload
                        title="Importing Excel File"
                        size="md"
                    >
                        <div className="space-y-4 px-6">
                            <div className="text-center">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Processing your Excel file... Please wait.
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    {uploadProgress}% complete
                                </p>
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Upload Results Modal */}
            <Modal
                isOpen={showUploadModal}
                onClose={handleModalClose}
                title="Bulk Upload Results"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2 flex-wrap">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {uploadResults.filter(r => r.status === 'success').length} New
                            </span>
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                {uploadResults.filter(r => r.status === 'updated').length} Updated
                            </span>
                            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                {uploadResults.filter(r => r.status === 'error').length} Failed
                            </span>
                            {uploadResults.filter(r => r.status === 'undone').length > 0 && (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                                    {uploadResults.filter(r => r.status === 'undone').length} Undone
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={downloadFinalReport}
                                className="flex items-center gap-2 text-xs"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Download Report
                            </Button>
                            {uploadResults.filter(r => r.status === 'error').length > 0 && (
                                <Button
                                    variant="secondary"
                                    onClick={downloadErrorReport}
                                    className="flex items-center gap-2 text-xs bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Download Errors
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Row
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Branch Code
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Message
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadResults.map((result, index) => (
                                    <tr
                                        key={index}
                                        className={`border-b border-gray-200 dark:border-gray-700 ${result.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' :
                                            result.status === 'updated' ? 'bg-blue-50 dark:bg-blue-900/10' :
                                                result.status === 'undone' ? 'bg-gray-50 dark:bg-gray-900/10' :
                                                    'bg-green-50 dark:bg-green-900/10'
                                            }`}
                                    >
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.rowNumber || index + 1}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.branchCode}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${result.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                                result.status === 'updated' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                                    result.status === 'undone' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' :
                                                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                }`}>
                                                {result.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {result.message}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button
                                variant="secondary"
                                onClick={handleUndo}
                                disabled={uploading}
                                className="bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                            >
                                Undo Updates
                            </Button>
                        )}
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button
                                variant="primary"
                                onClick={handleKeep}
                            >
                                Keep Changes
                            </Button>
                        )}
                        {(!undoStack.length || !uploadResults.some(r => r.status === 'updated')) && (
                            <Button variant="secondary" onClick={handleModalClose}>
                                Close
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}
